/**
 * Calculate distance between two geographical points using Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) *
        Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
};

const toRadians = (degrees) => {
    return degrees * (Math.PI / 180);
};

/**
 * City coordinates mapping
 */
export const CITY_COORDINATES = {
    "Bangalore": { lat: 12.9716, lon: 77.5946 },
    "Mumbai": { lat: 19.0760, lon: 72.8777 },
    "Delhi": { lat: 28.7041, lon: 77.1025 },
    "Hyderabad": { lat: 17.3850, lon: 78.4867 },
    "Chennai": { lat: 13.0827, lon: 80.2707 },
    "Kolkata": { lat: 22.5726, lon: 88.3639 },
    "Pune": { lat: 18.5204, lon: 73.8567 },
    "Ahmedabad": { lat: 23.0225, lon: 72.5714 },
};

/**
 * Get user's coordinates either from browser geolocation or selected city
 * @param {string} selectedCity - User's selected city
 * @returns {Promise<{lat: number, lon: number}>}
 */
export const getUserCoordinates = async (selectedCity) => {
    return new Promise((resolve) => {
        // Try to get browser geolocation first
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        lat: position.coords.latitude,
                        lon: position.coords.longitude,
                    });
                },
                () => {
                    // If geolocation fails, use selected city coordinates
                    const cityCoords = CITY_COORDINATES[selectedCity] || CITY_COORDINATES["Bangalore"];
                    resolve(cityCoords);
                },
                { timeout: 5000 }
            );
        } else {
            // If geolocation not supported, use selected city coordinates
            const cityCoords = CITY_COORDINATES[selectedCity] || CITY_COORDINATES["Bangalore"];
            resolve(cityCoords);
        }
    });
};

/**
 * Parse location string to extract coordinates
 * Handles various location formats from Firestore
 * @param {string|object} location - Event location data
 * @returns {{lat: number, lon: number}|null}
 */
export const parseEventLocation = (location) => {
    if (!location) return null;

    // If location is already an object with lat/lon
    if (typeof location === 'object' && location.lat && location.lon) {
        return { lat: location.lat, lon: location.lon };
    }

    // If location is an object with latitude/longitude
    if (typeof location === 'object' && location.latitude && location.longitude) {
        return { lat: location.latitude, lon: location.longitude };
    }

    // If location is a string (city name)
    if (typeof location === 'string') {
        return CITY_COORDINATES[location] || null;
    }

    return null;
};

/**
 * Filter events within a specified radius from user location
 * @param {Array} events - Array of event objects
 * @param {object} userCoords - User coordinates {lat, lon}
 * @param {number} radiusKm - Radius in kilometers (default: 50)
 * @returns {Array} Filtered events within radius
 */
export const filterEventsByDistance = (events, userCoords, radiusKm = 50) => {
    if (!userCoords || !events) return [];

    return events.filter(event => {
        const eventCoords = parseEventLocation(event.location || event.city);
        if (!eventCoords) return false;

        const distance = calculateDistance(
            userCoords.lat,
            userCoords.lon,
            eventCoords.lat,
            eventCoords.lon
        );

        return distance <= radiusKm;
    });
};
